import { Component, inject, ViewChild, ElementRef, AfterViewChecked, OnInit } from '@angular/core';
import { CommonModule, Location } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { FunctionService } from '../../services/function.service';
import { AIService } from '../../services/ai.service';
import { ClientService } from '../../services/client.service';
import { HtmlRendererComponent } from '../../shared/components/html-renderer/html-renderer.component';
// SchemaExplorerComponent import removed — now a standalone route

type ConversationMessage = { role: 'user' | 'assistant'; content: string };

interface ChatMessage {
  type: 'user' | 'clarify' | 'plan' | 'result' | 'loading' | 'error' | 'empty';
  content: string;
  timestamp: Date;
  rawHtml?: string;
  data?: any;
  sql?: string;
  suggestions?: string[];
}

@Component({
  selector: 'app-query',
  standalone: true,
  imports: [CommonModule, FormsModule, HtmlRendererComponent],
  templateUrl: './query.component.html',
})
export class QueryComponent implements AfterViewChecked, OnInit {
  private functionService = inject(FunctionService);
  private aiService = inject(AIService);
  private clientService = inject(ClientService);
  private location = inject(Location);
  private router = inject(Router);
  private route = inject(ActivatedRoute);

  @ViewChild('chatContainer') chatContainer: ElementRef | undefined;

  userQuery = '';
  chatHistory: ChatMessage[] = [];
  conversationMessages: ConversationMessage[] = [];
  isLoading = false;
  pendingExecution = false;
  executionStep = '';
  abortController: AbortController | null = null;

  // Hints
  showHintsPanel = false;
  hints: string[] = JSON.parse(localStorage.getItem('ai-query-hints') || '[]');
  newHint = '';

  // Visualization re-query on active result
  visualizationQuery = '';
  isVisualizationLoading = false;

  // Which result is shown on the left panel — null means "latest"
  pinnedResult: ChatMessage | null = null;

  // Flagging
  generatedQueries: string[] = [];
  isFlagging = false;
  flagged = false;
  showFlagPanel = false;
  flagNote = '';

  // Confirmation (thumbs-up)
  isConfirming = false;
  confirmed = false;

  // Dashboard (inline report below primary result)
  dashboardMode: 'auto' | 'off' = 'auto';
  isDashboardLoading = false;
  dashboardHtml: string | null = null;
  dashboardStep = '';
  dashboardAbort: AbortController | null = null;

  // Splits a plan message into bullet lines (strips leading • or - or *)
  planLines(content: string): string[] {
    return content
      .split('\n')
      .map(l => l.replace(/^[•\-\*]\s*/, '').trim())
      .filter(l => l.length > 0);
  }

  trackByIndex = (index: number) => index;

  get activeResult(): ChatMessage | null {
    for (let i = this.chatHistory.length - 1; i >= 0; i--) {
      if (this.chatHistory[i].type === 'result') return this.chatHistory[i];
    }
    return null;
  }

  get displayedResult(): ChatMessage | null {
    return this.pinnedResult ?? this.activeResult;
  }

  viewResult(msg: ChatMessage) {
    this.pinnedResult = msg;
  }

  ngOnInit() {
    this.route.queryParams.subscribe(p => {
      if (p['ask']) this.userQuery = p['ask'];
    });
    this.aiService.getSettings().then(s => { this.dashboardMode = s.dashboardMode; });
  }

  goBack() {
    this.location.back();
  }

  openExplorer() {
    this.router.navigate(['/schema-explorer']);
  }

  baseStyles = `
    * { box-sizing: border-box; }
    body { font-family: ui-sans-serif, system-ui, sans-serif; }
    img, svg { max-width: 100%; height: auto; }
    table { width: 100%; border-collapse: collapse; margin: 1em 0; }
    th, td { border: 1px solid #e5e7eb; padding: 8px 12px; text-align: left; font-size: 14px; }
    th { background: #f9fafb; font-weight: 600; color: #374151; }
    tr:nth-child(even) { background: #f9fafb; }
    tr:hover { background: #f0f9ff; }
    canvas { max-width: 100%; }
  `;

  ngAfterViewChecked() {
    if (this.chatContainer) {
      const el = this.chatContainer.nativeElement;
      el.scrollTop = el.scrollHeight;
    }
  }

  async sendMessage() {
    const text = this.userQuery.trim();
    if (!text || this.isLoading) return;

    this.userQuery = '';
    this.isLoading = true;
    this.pendingExecution = false;

    this.chatHistory.push({ type: 'user', content: text, timestamp: new Date() });
    this.conversationMessages.push({ role: 'user', content: text });

    const loadingMsg: ChatMessage = { type: 'loading', content: 'Analyzing your query...', timestamp: new Date() };
    this.chatHistory.push(loadingMsg);

    try {
      const result: any = await this.functionService.call('aiQuery', {
        messages: this.conversationMessages,
        clientId: this.clientService.selectedClientId,
        hints: this.hints,
      });

      this.chatHistory.pop();

      if (result.type === 'clarify') {
        this.chatHistory.push({ type: 'clarify', content: result.content, timestamp: new Date() });
        this.conversationMessages.push({ role: 'assistant', content: result.content });
      } else if (result.type === 'plan') {
        this.chatHistory.push({ type: 'plan', content: result.content, timestamp: new Date() });
        this.conversationMessages.push({ role: 'assistant', content: result.content });
        this.pendingExecution = true;
      }
    } catch (err) {
      this.chatHistory.pop();
      this.chatHistory.push({ type: 'error', content: 'Something went wrong. Please try again.', timestamp: new Date() });
      console.error(err);
    } finally {
      this.isLoading = false;
    }
  }

  executePlan() {
    this.isLoading = true;
    this.pendingExecution = false;
    this.executionStep = 'Fetching database schema...';
    this.abortController = new AbortController();

    const loadingMsg: ChatMessage = { type: 'loading', content: this.executionStep, timestamp: new Date() };
    const loadingIdx = this.chatHistory.length;
    this.chatHistory.push(loadingMsg);

    this.aiService.streamExecute(
      {
        messages: this.conversationMessages,
        clientId: this.clientService.selectedClientId,
        hints: this.hints,
      },
      (_step: string, message: string) => {
        this.executionStep = message;
        this.chatHistory[loadingIdx] = { type: 'loading', content: message, timestamp: new Date() };
      },
      (result: any) => {
        this.chatHistory.splice(loadingIdx, 1);
        this.chatHistory.push({
          type: 'result',
          content: result.summary || 'Here are your results.',
          timestamp: new Date(),
          rawHtml: result.html,
          data: result.data,
        });
        if (result.query) this.generatedQueries.push(result.query);
        this.conversationMessages.push({ role: 'assistant', content: result.summary || 'Results generated.' });
        this.pinnedResult = null;
        this.isLoading = false;
        this.executionStep = '';
        this.abortController = null;
        if (this.dashboardMode === 'auto') {
          this.loadDashboard(result.data, result.query ?? '');
        }
      },
      (query: string, message: string) => {
        this.chatHistory.splice(loadingIdx, 1);
        this.chatHistory.push({ type: 'empty', content: message, timestamp: new Date(), sql: query });
        if (query) this.generatedQueries.push(query);
        this.isLoading = false;
        this.executionStep = '';
        this.abortController = null;
      },
      (message: string) => {
        this.chatHistory.splice(loadingIdx, 1);
        this.chatHistory.push({ type: 'error', content: message, timestamp: new Date() });
        this.isLoading = false;
        this.executionStep = '';
        this.abortController = null;
      },
      this.abortController.signal,
      (questions: string[]) => {
        // Attach suggestions to the last result message
        const lastResult = this.chatHistory.slice().reverse().find(m => m.type === 'result');
        if (lastResult) lastResult.suggestions = questions;
      }
    );
  }

  useSuggestion(question: string) {
    this.userQuery = question;
  }

  cancelExecution() {
    this.abortController?.abort();
    this.abortController = null;
    this.chatHistory.pop(); // remove loading bubble
    this.isLoading = false;
    this.executionStep = '';
    this.pendingExecution = false;
  }

  cancelPlan() {
    this.pendingExecution = false;
    const lastPlanIdx = this.chatHistory.map(m => m.type).lastIndexOf('plan');
    if (lastPlanIdx >= 0) this.chatHistory.splice(lastPlanIdx, 1);
    const lastAssistantIdx = this.conversationMessages.map(m => m.role).lastIndexOf('assistant');
    if (lastAssistantIdx >= 0) this.conversationMessages.splice(lastAssistantIdx, 1);
  }

  loadDashboard(primaryResult: any, sql = '') {
    this.dashboardHtml = null;
    this.isDashboardLoading = true;
    this.dashboardStep = 'Building dashboard...';
    this.dashboardAbort = new AbortController();

    this.aiService.streamDashboard(
      {
        messages: this.conversationMessages,
        clientId: this.clientService.selectedClientId,
        primaryResult,
        sql,
      },
      (_step: string, message: string) => { this.dashboardStep = message; },
      (html: string) => {
        this.dashboardHtml = html;
        this.isDashboardLoading = false;
        this.dashboardStep = '';
        this.dashboardAbort = null;
      },
      (_msg: string) => {
        this.isDashboardLoading = false;
        this.dashboardStep = '';
        this.dashboardAbort = null;
      },
      this.dashboardAbort.signal
    );
  }

  async toggleDashboardMode() {
    this.dashboardMode = this.dashboardMode === 'auto' ? 'off' : 'auto';
    await this.aiService.saveSettings({ dashboardMode: this.dashboardMode });
  }

  async executeVisualizationQuery() {
    if (!this.visualizationQuery.trim() || !this.displayedResult) return;
    this.isVisualizationLoading = true;
    try {
      const result: any = await this.functionService.call('generateHtml', {
        userQuery: this.visualizationQuery,
        data: this.displayedResult.data,
      });
      if (result.html) {
        this.displayedResult.rawHtml = result.html;
        this.visualizationQuery = '';
      }
    } catch (err) {
      console.error(err);
    } finally {
      this.isVisualizationLoading = false;
    }
  }

  addHint() {
    const h = this.newHint.trim();
    if (!h) return;
    this.hints = [...this.hints, h];
    this.newHint = '';
    localStorage.setItem('ai-query-hints', JSON.stringify(this.hints));
  }

  removeHint(index: number) {
    this.hints = this.hints.filter((_, i) => i !== index);
    localStorage.setItem('ai-query-hints', JSON.stringify(this.hints));
  }

  injectTestChart() {
    const html = `
<style>
  .wrap { font-family: sans-serif; padding: 16px; }
  h3 { margin: 0 0 12px; font-size: 15px; color: #374151; }
</style>
<div class="wrap">
  <h3>Leads by City (sample)</h3>
  <canvas id="testChart" width="600" height="300"></canvas>
</div>
<script src="https://cdn.jsdelivr.net/npm/chart.js"></script>
<script>
  var ctx = getContext('#testChart', '2d');
  new Chart(ctx, {
    type: 'bar',
    data: {
      labels: ['Mumbai', 'Delhi', 'Bangalore', 'Chennai', 'Hyderabad', 'Pune', 'Kolkata'],
      datasets: [{
        label: 'Leads',
        data: [142, 118, 95, 73, 61, 48, 37],
        backgroundColor: '#6366f1'
      }]
    },
    options: {
      responsive: false,
      animation: false,
      plugins: { legend: { display: false } },
      scales: { y: { beginAtZero: true } }
    }
  });
</script>`;
    this.chatHistory.push({
      type: 'result',
      content: 'Test chart injected.',
      timestamp: new Date(),
      rawHtml: html,
      data: null,
    });
  }

  resetConversation() {
    this.chatHistory = [];
    this.conversationMessages = [];
    this.generatedQueries = [];
    this.pendingExecution = false;
    this.userQuery = '';
    this.pinnedResult = null;
    this.flagged = false;
    this.showFlagPanel = false;
    this.flagNote = '';
    this.confirmed = false;
    this.isConfirming = false;
    this.dashboardHtml = null;
    this.isDashboardLoading = false;
    this.dashboardAbort?.abort();
    this.dashboardAbort = null;
  }

  async submitConfirm() {
    if (this.isConfirming || this.confirmed || !this.generatedQueries.length) return;
    this.isConfirming = true;
    try {
      await this.functionService.call('confirmResult', {
        clientId: this.clientService.selectedClientId,
        messages: this.conversationMessages,
        queries: this.generatedQueries,
      });
      this.confirmed = true;
    } catch (err) {
      console.error('Failed to confirm result:', err);
    } finally {
      this.isConfirming = false;
    }
  }

  async submitFlag() {
    if (this.isFlagging || this.flagged) return;
    this.isFlagging = true;
    try {
      await this.functionService.call('flagConversation', {
        clientId: this.clientService.selectedClientId,
        messages: this.conversationMessages,
        queries: this.generatedQueries,
        userNote: this.flagNote.trim(),
        source: 'editor/aiQuery',
      });
      this.flagged = true;
      this.showFlagPanel = false;
      this.flagNote = '';
    } catch (err) {
      console.error('Failed to flag conversation:', err);
    } finally {
      this.isFlagging = false;
    }
  }
}
